import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import CustomButton from "./../../utility-v1/Button";
import OrderSummary from "./OrderSummary";
import { FONT_FAMILY } from "../../config/themeConstants"; // Import FONT_FAMILY
import { GlobalContext } from "./../../GlobalProvider";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { API_BASE_URL } from "../../config/constants";
import DeliveryDetails from "./DeliveryDetails";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { useToast } from "./../../utility-v1/ToastProvider";
import PaymentSecureBox from "./PaymentSecureBox"; // Adjust path as needed
import { isMobile, isTablet } from "react-device-detect";
import LoadingOverlay from "../../components/Loading/LoadingOverlay"; // relative to Checkout.js location
import StoredCartsUpdate from "./../UtilityClasses/StoredCartsUpdate";
import { IS_PHONEPE_ENABLED } from "../../config/paymentFlags";
import GPayInline from "./GPayInline";

// Extend dayjs with the localizedFormat plugin
dayjs.extend(localizedFormat);

export default function Checkout({ user, allItems }) {
  const [showGPayConfirmation, setShowGPayConfirmation] = useState(false);
  const [showGPayPopup, setShowGPayPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { handleShowToast } = useToast();
  const { globalVariable } = useContext(GlobalContext);
  const [storedCart, setStoredCart] = useState([]);
  const [comboCart, setComboCart] = useState([]);
  const location = useLocation();
  const navigate = useNavigate(); // Initialize the useNavigate hook
  const queryParams = new URLSearchParams(location.search);
  const isBuyNow = queryParams.get("buynow") === "true";
  const [salesId, setSalesId] = useState("");
  const [smsTracking, setSmsracking] = useState("");
  const [autoDiscounts, setAutoDiscounts] = useState({
    itemCountDiscount: false,
    totalPriceDiscount: false,
  });

  const [form, setForm] = useState({
    items: {},
    name: "",
    salesDate: dayjs(), // Set to current date initially
    price: "",
    buyerDetails: "",
    phoneNumber: "",
    salesStatus: "SP", // Default status set to "SP"
    systemDate: dayjs(), // Adding system date
    giveAway: false, // Adding give away checkbox state
    pincode: "",
    state: "KL",
    coupon: "",
    email: "",
    additionalDiscount: [],
    shipping: "",
    upiLastFour: "1111",
    salesType: "NORMAL",
    createdSource: "MOBILE",
    createdFrom: "WEB",
  });

  const [errors, setErrors] = useState({
    name: false,
    buyerDetails: false,
    pincode: false,
    state: false,
    phoneNumber: false,
    upiLastFour: false,
    email: false,
  });

  // Function to log the form's updated items
  useEffect(() => {
    // Scroll to the top when the component is loaded
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    if (isBuyNow) {
      if (!globalVariable.cartItems) {
        handleShowToast("Refreshing prohibitted.", "warning");
        navigate(`/jewels/list/All`);
      }
      setStoredCart(globalVariable.cartItems);
      setComboCart(globalVariable.comboCart);
    } else {
      if (!localStorage.getItem("cartItems")) {
        handleShowToast("Refreshing prohibitted.", "warning");
        navigate(`/jewels/list/All`);
      }
      // Check and assign empty object if "cartItems" is null
      const storedCart = StoredCartsUpdate.updateCartByQuantityLeftCheck(
        allItems
      );
      setStoredCart(storedCart);

      // Check and assign empty object if "comboCart" is null
      const comboCartItems =
        JSON.parse(localStorage.getItem("comboCart")) || {};
      setComboCart(comboCartItems);
    }
  });

  // ✅ **Generate Unique Transaction ID (Alphanumeric, 35 chars max)**
  function generateTransactionId() {
    const timestamp = Date.now(); // Current timestamp in milliseconds
    const uniqueId = `IM-${timestamp}`;
    return uniqueId.substring(0, 35); // Ensure it's within 35 characters
  }

  const fetchPhonePeAuthToken = async (transactionId) => {
    try {
      // Step 1: Call the backend to fetch the auth token.
      const response = await axios.post(
        `${API_BASE_URL}/phonepe/fetch-auth-token`
      );

      // Check if the response includes the access token.
      if (response.data.access_token) {
        const authToken = response.data.access_token;
        const expiresAt = response.data.expires_at; // Unix timestamp (in seconds)

        // Store the token and its expiry in localStorage for later use.
        localStorage.setItem("phonepe_auth_token", authToken);
        localStorage.setItem("phonepe_auth_token_expires_at", expiresAt);
        // Explanation:
        // expires_at indicates the time when the token will no longer be valid.
        // It is provided as a Unix timestamp (seconds since January 1, 1970).
        // Before making API calls, compare the current time with expires_at.
        // If the current time is greater than or equal to expires_at, the token has expired.

        // Get the current time in seconds.
        const currentTimeSeconds = Math.floor(Date.now() / 1000);

        if (currentTimeSeconds >= expiresAt) {
          // Token expired; display a message.
          console.error("Auth token expired.");
        } else {
          // Token is still valid; proceed to call the initiate fund API.
          console.log(
            "Auth token is valid. Proceeding to initiate fund API..."
          );
          initiateFundApi(transactionId); // Replace with your actual initiate fund API function.
        }

        return response.data;
      } else {
        setIsLoading(false);
        console.error("Failed to fetch auth token:", response.data);
        alert("Auth token fetch failed.");
      }
    } catch (error) {
      console.error("PhonePe Auth Token Error:", error);
      alert("Error fetching auth token.");
    } finally {
    }
  };

  const initiateFundApi = async (transactionId) => {
    try {
      localStorage.setItem("transactionId", transactionId);
      // Retrieve the access token from local storage.
      const accessToken = localStorage.getItem("phonepe_auth_token");
      if (!accessToken) {
        alert("Access token not found. Please fetch the auth token first.");
        return;
      }

      // Build the payment initiation payload.
      const payload = {
        amount: form.price, // Amount in paisa
        transactionId, // Unique order ID
        customerMobile: form.phoneNumber,
        redirectUrl: `https://www.illolam.com/jewels/confirmation?transactionId=${transactionId}`,
        email: form.email,
        name: form.name,
        accessToken, // Pass the access token for backend validation
      };

      const response = await axios.post(
        `${API_BASE_URL}/phonepe/initiate-payment`,
        payload
      );

      console.log("Payment Response:", response.data);

      const redirectUrl = response.data.redirectUrl;
      console.log("Redirect URL:", redirectUrl);

      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        console.error("Failed to initiate payment:", response.data);
        alert("Payment initiation failed.");
      }
    } catch (error) {
      console.error(
        "PhonePe Payment Error:",
        error.response ? error.response.data : error.message
      );
      alert("Error initiating payment.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Clear the error for the specific field being updated
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: false,
    }));

    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "Phone number is required.";
    if (!/^[1-9][0-9]{9}$/.test(phoneNumber)) {
      if (phoneNumber.length !== 10) {
        return "Phone number must be exactly 10 digits.";
      }
      if (!/^[0-9]+$/.test(phoneNumber)) {
        return "Phone number must contain only numbers.";
      }
      if (phoneNumber.startsWith("0")) {
        return "Phone number cannot start with 0.";
      }
    }
    return ""; // ✅ No errors
  };

  const validateEmail = (email) => {
    if (!email) return "Email is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? "" : "Invalid email format.";
  };

  const validateForm = () => {
    const newErrors = {
      name: form.name.trim() === "" ? "Name is required." : "",
      buyerDetails:
        form.buyerDetails.trim() === "" ? "Buyer details are required." : "",
      pincode: form.pincode.trim() === "" ? "Pincode is required." : "",
      state: form.state.trim() === "" ? "State is required." : "",
      phoneNumber: validatePhoneNumber(form.phoneNumber), // ✅ Now using validation function
      email: validateEmail(form.email), // ✅ added email validation
    };

    setErrors(newErrors);

    return !Object.values(newErrors).some((error) => error !== ""); // ✅ Returns true if no errors
  };

  // Function to handle setting items in the form
  const setItemsInForm = (itemsObject) => {
    setForm({ ...form, items: itemsObject });
  };

  const setCartPriceInForm = (itemsObject) => {
    setForm((form) => ({
      ...form,
      price: itemsObject, // Update form.items with the object from OrderSummary
    }));
  };

  const handleConfirm = () => {
    const systemDate = dayjs().format("YYYY-MM-DD HH:mm:ss");
    const salesDate = dayjs().format("YYYY-MM-DD HH:mm:ss");
    const shipmentDate = form.shipmentDate
      ? form.shipmentDate.format("YYYY-MM-DD")
      : null;
    let source = "DESKTOP"; // Default
    if (isMobile) {
      source = "MOBILE";
    } else if (isTablet) {
      source = "TABLET";
    }

    form.additionalDiscount = [];
    autoDiscounts.totalPriceDiscount &&
      form.additionalDiscount.push("TOTALPRICE");
    autoDiscounts.itemCountDiscount &&
      form.additionalDiscount.push("ITEMCOUNT");
    let transactionId = generateTransactionId();
    const newSale = {
      ...form,
      salesDate,
      systemDate,
      shipmentDate,
      salesStatus: "SP", //Sale Pending, just Initiated
      createdSource: source,
      additionalDiscount: form.additionalDiscount,
      shipping: form.state === "KL" ? 0 : 50, // Set shipping to 50 if state is KL, otherwise 0
      upiLastFour: transactionId,
    };

    axios
      .post(`${API_BASE_URL}/sales`, newSale)
      .then((response) => {
        if (!response.data || !response.data.id) {
          throw new Error("Sales ID is missing in the response");
        }
        const salesId = response.data.id; // ✅ Ensure we get a valid sales ID
        transactionId = response.data.upi_transaction_id; // ✅ Ensure we get a valid sales ID
        setSmsracking(transactionId);
        setSalesId(salesId);

        // Prepare the payload for the /salesrecord/insert endpoint
        const salesRecordData = {
          id: salesId, // The newly created sales ID
          items: form.items, // Items from the form
          combos: comboCart,
        };

        return axios
          .post(`${API_BASE_URL}/salesrecord/insert`, salesRecordData)
          .then(() => salesId); // ✅ Pass salesId to next .then()
      })
      .then((salesId) => {
        //  user && initiatePhonePePayment(salesId);
        if (IS_PHONEPE_ENABLED) {
          fetchPhonePeAuthToken(transactionId);
        } else {
          //   setShowGPayPopup(true);
          setShowGPayConfirmation(true);
          setIsLoading(false);
        }

        // ✅ Ensure salesId is available before navigating
      })
      .catch((error) => {
        console.error("Error adding sale or inserting sales record:", error);
      });
  };

  const setCouponInForm = (coupon) => {
    setForm({ ...form, coupon: coupon });
  };

  const handleSaveSales = () => {
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    handleConfirm();
    window.scrollTo({ top: 0 });
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography
        sx={{
          fontFamily: FONT_FAMILY,
          fontSize: "20px",
          fontWeight: 600,
          color: "green",
          py: "10px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {showGPayConfirmation && (
          <Box component="span" sx={{ color: "green" }}>
            ✅
          </Box>
        )}
        {showGPayConfirmation
          ? "We've received your order."
          : "Let’s get this order placed."}
      </Typography>

      <Grid container spacing={3}>
        {!showGPayConfirmation && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <DeliveryDetails
                form={form}
                handleChange={handleChange}
                isEditMode={false}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: "16px",
                  fontWeight: 600,
                  marginBottom: { xs: "20px", lg: "25px" },
                }}
              >
                Order Summary
              </Typography>
              <OrderSummary
                setItems={setItemsInForm}
                setCartPrice={setCartPriceInForm}
                cartItems={storedCart}
                action="Checkout"
                setCoupon={setCouponInForm}
                coupon={form.coupon}
                comboCart={comboCart}
                deliveryState={form.state}
                setAutoDiscountsInParent={setAutoDiscounts}
              />
              {IS_PHONEPE_ENABLED && <PaymentSecureBox />}
              <Box sx={{ marginTop: { xs: "20px" } }}>
                <CustomButton
                  label="Place Order"
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSaveSales}
                />
              </Box>
            </Grid>
          </>
        )}

        {showGPayConfirmation && (
          <Grid item xs={12}>
            <GPayInline
              form={form}
              smsTracking={smsTracking}
              onEditAddress={() => setShowGPayConfirmation(false)}
            />
          </Grid>
        )}
      </Grid>

      {isLoading && <LoadingOverlay />}
    </Box>
  );
}
